import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { formatDate, formatCurrency } from '@/lib/utils';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Receipt,
  Plus,
  Search,
  ChevronLeft,
  ChevronRight,
  Loader2,
  DollarSign,
  Hash,
  TrendingUp,
  CheckCircle,
  RefreshCw,
  Download,
  Upload,
  FileText,
  AlertCircle,
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import {
  parseCsvFile,
  expensesToCsv,
  generateCsvTemplate,
  downloadCsv,
  type ParseResult,
} from '@/lib/expense-csv';
import { ExpenseReceiptsPanel } from './expense-receipts-panel';
import { ExpenseAuditReportDialog } from './expense-audit-report-dialog';
import { FileDown } from 'lucide-react';

interface Expense {
  id: string;
  categoryId: string | null;
  categoryName: string;
  categorySlug: string;
  businessProfileId: string | null;
  businessProfileName: string | null;
  description: string;
  amount: number;
  expenseDate: string;
  vendor: string | null;
  referenceNumber: string | null;
  isRecurring: boolean;
  recurrenceFrequency: string | null;
  isTaxDeductible: boolean;
  taxCategory: string | null;
  paymentDueDay: number | null;
  notes: string | null;
  chargeAccountId: string | null;
  chargeAccountName: string | null;
  assetId: string | null;
  assetName: string | null;
  assetType: string | null;
  vehicleMethod: 'mileage' | 'actual' | null;
  businessMiles: number | null;
  totalMiles: number | null;
  source: 'expense' | 'subscription' | 'loan';
}

interface ExpenseCategory {
  id: string;
  name: string;
  slug: string;
  isSystem: boolean;
  taxDeductionClass: string | null;
  isInventory: boolean;
}

interface BusinessProfile {
  id: string;
  name: string;
}

interface ChargeAccountOption {
  id: string;
  name: string;
  issuer: string | null;
  last4: string | null;
  status: string;
}

// Categories that cannot be paid with a credit card (mortgage, amortizing loans,
// and the card's own interest expense). Keep in sync with sp_fin_create_expense /
// sp_fin_update_expense guardrail.
const BLOCKED_CARD_CATEGORY_SLUGS = new Set(['mortgage', 'loans', 'credit_card_interest']);

interface ExpenseSummaryData {
  summary: {
    totalSpend: number;
    categoryCount: number;
    expenseCount: number;
    taxDeductibleTotal: number;
  };
  categories: Array<{
    categoryName: string;
    categorySlug: string;
    totalAmount: number;
    expenseCount: number;
  }>;
}

function getDatePreset(preset: string): { from: string; to: string } {
  const now = new Date();
  const to = now.toISOString().split('T')[0];
  let from: string;
  switch (preset) {
    case 'week': {
      const d = new Date(now);
      d.setDate(d.getDate() - d.getDay());
      from = d.toISOString().split('T')[0];
      break;
    }
    case 'month':
      from = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
      break;
    case 'year':
      from = new Date(now.getFullYear(), 0, 1).toISOString().split('T')[0];
      break;
    default:
      from = '';
  }
  return { from, to };
}

export function ExpensesPage() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [profileFilter, setProfileFilter] = useState('');
  const [datePreset, setDatePreset] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [page, setPage] = useState(1);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingExpense, setEditingExpense] = useState<Expense | null>(null);
  // Track category + recurring state so we can show/hide the card selector.
  const [formCategoryId, setFormCategoryId] = useState<string>('');
  const [formAssetId, setFormAssetId] = useState<string>('');
  const [formVehicleMethod, setFormVehicleMethod] = useState<'mileage' | 'actual' | ''>('');
  const [formIsRecurring, setFormIsRecurring] = useState<boolean>(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importParseResult, setImportParseResult] = useState<ParseResult | null>(null);
  const [importFileName, setImportFileName] = useState('');
  const [auditDialogOpen, setAuditDialogOpen] = useState(false);

  function handlePresetChange(preset: string) {
    setDatePreset(preset);
    if (preset === 'all') {
      setDateFrom('');
      setDateTo('');
    } else if (preset !== 'custom') {
      const { from, to } = getDatePreset(preset);
      setDateFrom(from);
      setDateTo(to);
    }
    setPage(1);
  }

  const { data: categoriesData } = useQuery({
    queryKey: ['expenseCategories'],
    queryFn: () => api.getExpenseCategories(),
  });

  const { data: profilesData } = useQuery({
    queryKey: ['businessProfiles'],
    queryFn: () => api.getBusinessProfiles(),
  });

  const { data: chargeAccountsData } = useQuery({
    queryKey: ['chargeAccounts', { forAllocator: true }],
    queryFn: () => api.getChargeAccounts({ status: 'active', limit: 100 }),
  });

  const { data: assetsData } = useQuery({
    queryKey: ['assets', { forExpensePicker: true }],
    queryFn: () => api.getAssets({ status: 'active', limit: 200 }),
  });

  const { data: summaryData } = useQuery({
    queryKey: ['expenseSummary', { dateFrom, dateTo, businessProfileId: profileFilter }],
    queryFn: () => api.getExpenseSummary({
      dateFrom: dateFrom || undefined,
      dateTo: dateTo || undefined,
      businessProfileId: profileFilter || undefined,
    }),
  });

  const { data, isLoading } = useQuery({
    queryKey: ['expenses', { page, search, categoryId: categoryFilter, businessProfileId: profileFilter, dateFrom, dateTo }],
    queryFn: () => api.getExpenses({
      page,
      limit: 20,
      search: search || undefined,
      categoryId: categoryFilter || undefined,
      businessProfileId: profileFilter || undefined,
      dateFrom: dateFrom || undefined,
      dateTo: dateTo || undefined,
    }),
  });

  const categories = (categoriesData?.data || []) as ExpenseCategory[];
  const profiles = (profilesData?.data || []) as BusinessProfile[];
  const chargeAccounts = (chargeAccountsData?.data || []) as ChargeAccountOption[];
  const assets = (assetsData?.data || []) as Array<{ id: string; name: string; assetType: string }>;
  const summary = (summaryData?.data as ExpenseSummaryData)?.summary;
  const topCategories = (summaryData?.data as ExpenseSummaryData)?.categories || [];
  const expenses = (data?.data || []) as Expense[];
  const pagination = data?.pagination;

  const createMutation = useMutation({
    mutationFn: (data: Record<string, unknown>) => api.createExpense(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['expenses'] });
      queryClient.invalidateQueries({ queryKey: ['expenseSummary'] });
      queryClient.invalidateQueries({ queryKey: ['chargeAccountAllocations'] });
      setDialogOpen(false);
      toast({ title: 'Expense created' });
    },
    onError: () => toast({ title: 'Failed to create expense', variant: 'destructive' }),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Record<string, unknown> }) => api.updateExpense(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['expenses'] });
      queryClient.invalidateQueries({ queryKey: ['expenseSummary'] });
      queryClient.invalidateQueries({ queryKey: ['chargeAccountAllocations'] });
      setDialogOpen(false);
      setEditingExpense(null);
      toast({ title: 'Expense updated' });
    },
    onError: () => toast({ title: 'Failed to update expense', variant: 'destructive' }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.deleteExpense(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['expenses'] });
      queryClient.invalidateQueries({ queryKey: ['expenseSummary'] });
      queryClient.invalidateQueries({ queryKey: ['chargeAccountAllocations'] });
      toast({ title: 'Expense deleted' });
    },
    onError: () => toast({ title: 'Failed to delete expense', variant: 'destructive' }),
  });

  const importMutation = useMutation({
    mutationFn: (rows: Array<Record<string, unknown>>) => api.importExpenses(rows),
    onSuccess: (result) => {
      const data = result?.data as { created: number; failed: number; total: number } | undefined;
      queryClient.invalidateQueries({ queryKey: ['expenses'] });
      queryClient.invalidateQueries({ queryKey: ['expenseSummary'] });
      queryClient.invalidateQueries({ queryKey: ['chargeAccountAllocations'] });
      if (data) {
        if (data.failed > 0) {
          toast({
            title: `Imported ${data.created} of ${data.total}`,
            description: `${data.failed} row${data.failed !== 1 ? 's' : ''} failed`,
            variant: 'destructive',
          });
        } else {
          toast({ title: `Imported ${data.created} expense${data.created !== 1 ? 's' : ''}` });
        }
      }
      setImportDialogOpen(false);
      setImportParseResult(null);
      setImportFileName('');
    },
    onError: (err) => {
      toast({
        title: 'Import failed',
        description: err instanceof Error ? err.message : 'Unknown error',
        variant: 'destructive',
      });
    },
  });

  async function handleFileSelected(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setImportFileName(file.name);
    try {
      const result = await parseCsvFile(file, categories, profiles);
      setImportParseResult(result);
    } catch (err) {
      toast({
        title: 'Could not parse CSV',
        description: err instanceof Error ? err.message : 'Unknown error',
        variant: 'destructive',
      });
    }
    // Reset input so re-selecting the same file works
    e.target.value = '';
  }

  function handleImportConfirm() {
    if (!importParseResult || importParseResult.valid.length === 0) return;
    importMutation.mutate(importParseResult.valid as unknown as Array<Record<string, unknown>>);
  }

  function handleDownloadTemplate() {
    const sampleCategory = categories.find(c => c.slug === 'utilities')?.name || categories[0]?.name || 'Utilities';
    const csv = generateCsvTemplate(sampleCategory);
    downloadCsv(csv, 'expense-import-template.csv');
  }

  function handleExportCurrent() {
    const rows = expenses.map((e) => ({
      description: e.description,
      amount: e.amount,
      expenseDate: e.expenseDate,
      categoryName: e.categoryName,
      vendor: e.vendor,
      referenceNumber: e.referenceNumber,
      isRecurring: e.isRecurring,
      recurrenceFrequency: e.recurrenceFrequency,
      paymentDueDay: e.paymentDueDay,
      isTaxDeductible: e.isTaxDeductible,
      taxCategory: e.taxCategory,
      businessProfileName: e.businessProfileName,
      notes: e.notes,
    }));
    const csv = expensesToCsv(rows);
    const today = new Date().toISOString().split('T')[0];
    downloadCsv(csv, `expenses-${today}.csv`);
  }

  function openImportDialog() {
    setImportParseResult(null);
    setImportFileName('');
    setImportDialogOpen(true);
  }

  function openCreate() {
    setEditingExpense(null);
    setFormCategoryId('');
    setFormIsRecurring(false);
    setFormAssetId('');
    setFormVehicleMethod('');
    setDialogOpen(true);
  }

  function openEdit(expense: Expense) {
    setEditingExpense(expense);
    setFormCategoryId(expense.categoryId || '');
    setFormIsRecurring(expense.isRecurring);
    setFormAssetId(expense.assetId || '');
    setFormVehicleMethod(expense.vehicleMethod || '');
    setDialogOpen(true);
  }

  function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const categoryId = formData.get('categoryId') as string | null;
    const categorySlug = categories.find(c => c.id === categoryId)?.slug;
    const isBlockedForCard = categorySlug ? BLOCKED_CARD_CATEGORY_SLUGS.has(categorySlug) : false;
    const rawChargeAccountId = formData.get('chargeAccountId') as string | null;
    const chargeAccountId = isBlockedForCard ? null : (rawChargeAccountId || null);

    const payload: Record<string, unknown> = {
      categoryId,
      businessProfileId: formData.get('businessProfileId') || null,
      description: formData.get('description'),
      amount: parseFloat(formData.get('amount') as string),
      expenseDate: formData.get('expenseDate') || null,
      vendor: formData.get('vendor') || null,
      referenceNumber: formData.get('referenceNumber') || null,
      isRecurring: formData.get('isRecurring') === 'on',
      recurrenceFrequency: formData.get('recurrenceFrequency') || null,
      isTaxDeductible: formData.get('isTaxDeductible') === 'on',
      // taxCategory is no longer set per-expense — it's derived from the
      // category's tax_deduction_class at form-render time.
      taxCategory: null,
      paymentDueDay: formData.get('paymentDueDay') ? parseInt(formData.get('paymentDueDay') as string) : null,
      chargeAccountId,
      notes: formData.get('notes') || null,
      // Linked asset + vehicle metadata. Asset is optional on any expense;
      // vehicle_method/miles only meaningful when the linked asset is a vehicle.
      assetId: formAssetId || null,
      vehicleMethod: formVehicleMethod || null,
      businessMiles: formData.get('businessMiles')
        ? parseInt(formData.get('businessMiles') as string, 10)
        : null,
      totalMiles: formData.get('totalMiles')
        ? parseInt(formData.get('totalMiles') as string, 10)
        : null,
    };

    if (editingExpense) {
      updateMutation.mutate({ id: editingExpense.id, data: payload });
    } else {
      createMutation.mutate(payload);
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-end gap-2">
        <Button variant="outline" onClick={handleDownloadTemplate}>
          <FileText className="h-4 w-4 mr-2" />
          Download Template
        </Button>
        <Button variant="outline" onClick={handleExportCurrent} disabled={expenses.length === 0}>
          <Download className="h-4 w-4 mr-2" />
          Export CSV
        </Button>
        <Button variant="outline" onClick={() => setAuditDialogOpen(true)}>
          <FileDown className="h-4 w-4 mr-2" />
          Audit Report
        </Button>
        <Button variant="outline" onClick={openImportDialog}>
          <Upload className="h-4 w-4 mr-2" />
          Import CSV
        </Button>
        <Button onClick={openCreate}>
          <Plus className="h-4 w-4 mr-2" />
          New Common Expense
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-primary/10"><DollarSign className="h-5 w-5 text-primary" /></div>
              <div>
                <p className="text-sm text-muted-foreground">Total Spend</p>
                <p className="text-xl font-bold">{formatCurrency(summary?.totalSpend || 0)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-500/10"><Hash className="h-5 w-5 text-blue-500" /></div>
              <div>
                <p className="text-sm text-muted-foreground">Expenses</p>
                <p className="text-xl font-bold">{summary?.expenseCount || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-orange-500/10"><TrendingUp className="h-5 w-5 text-orange-500" /></div>
              <div>
                <p className="text-sm text-muted-foreground">Top Category</p>
                <p className="text-xl font-bold">{topCategories[0]?.categoryName || '—'}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-green-500/10"><CheckCircle className="h-5 w-5 text-green-500" /></div>
              <div>
                <p className="text-sm text-muted-foreground">Tax Deductible</p>
                <p className="text-xl font-bold">{formatCurrency(summary?.taxDeductibleTotal || 0)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-4">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search expenses..."
            className="pl-9"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
          />
        </div>
        <Select value={datePreset} onValueChange={handlePresetChange}>
          <SelectTrigger className="w-36">
            <SelectValue placeholder="Time period" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Time</SelectItem>
            <SelectItem value="week">This Week</SelectItem>
            <SelectItem value="month">This Month</SelectItem>
            <SelectItem value="year">This Year</SelectItem>
            <SelectItem value="custom">Custom</SelectItem>
          </SelectContent>
        </Select>
        {datePreset === 'custom' && (
          <>
            <Input type="date" className="w-40" value={dateFrom} onChange={(e) => { setDateFrom(e.target.value); setPage(1); }} />
            <Input type="date" className="w-40" value={dateTo} onChange={(e) => { setDateTo(e.target.value); setPage(1); }} />
          </>
        )}
        <Select value={categoryFilter} onValueChange={(v) => { setCategoryFilter(v === 'all' ? '' : v); setPage(1); }}>
          <SelectTrigger className="w-44">
            <SelectValue placeholder="All categories" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All categories</SelectItem>
            {categories.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={profileFilter} onValueChange={(v) => { setProfileFilter(v === 'all' ? '' : v); setPage(1); }}>
          <SelectTrigger className="w-44">
            <SelectValue placeholder="All profiles" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All profiles</SelectItem>
            {profiles.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      {isLoading ? (
        <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin" /></div>
      ) : expenses.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Receipt className="h-12 w-12 text-muted-foreground mb-4" />
            <p className="text-muted-foreground">No expenses found</p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <table className="w-full">
              <thead>
                <tr className="border-b bg-muted/50">
                  <th className="text-left p-3 text-sm font-medium">Date</th>
                  <th className="text-left p-3 text-sm font-medium">Description</th>
                  <th className="text-left p-3 text-sm font-medium">Category</th>
                  <th className="text-left p-3 text-sm font-medium">Business Profile</th>
                  <th className="text-left p-3 text-sm font-medium">Vendor</th>
                  <th className="text-right p-3 text-sm font-medium">Amount</th>
                  <th className="text-left p-3 text-sm font-medium">Paid with</th>
                  <th className="text-center p-3 text-sm font-medium">Tax Ded.</th>
                  <th className="text-right p-3 text-sm font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {expenses.map((exp) => (
                  <tr key={`${exp.source}-${exp.id}`} className="border-b hover:bg-muted/30">
                    <td className="p-3 text-sm">{formatDate(exp.expenseDate)}</td>
                    <td className="p-3 text-sm font-medium">
                      <span className="flex items-center gap-2">
                        {exp.source === 'subscription' && (
                          <RefreshCw className="h-3.5 w-3.5 text-blue-500 shrink-0" />
                        )}
                        {exp.source === 'loan' && (
                          <RefreshCw className="h-3.5 w-3.5 text-amber-500 shrink-0" />
                        )}
                        {exp.description}
                      </span>
                    </td>
                    <td className="p-3">
                      <Badge variant={exp.source !== 'expense' ? 'default' : 'secondary'}>
                        {exp.categoryName}
                      </Badge>
                    </td>
                    <td className="p-3 text-sm">{exp.businessProfileName || '—'}</td>
                    <td className="p-3 text-sm">{exp.vendor || '—'}</td>
                    <td className="p-3 text-sm text-right font-medium">{formatCurrency(exp.amount)}</td>
                    <td className="p-3 text-sm">
                      {exp.chargeAccountName ? (
                        <Badge variant="secondary">{exp.chargeAccountName}</Badge>
                      ) : (
                        <span className="text-xs text-muted-foreground">—</span>
                      )}
                    </td>
                    <td className="p-3 text-center">
                      {exp.isTaxDeductible && <CheckCircle className="h-4 w-4 text-green-500 inline" />}
                    </td>
                    <td className="p-3 text-right">
                      {exp.source === 'subscription' ? (
                        <span className="text-xs text-muted-foreground">Manage in Subscriptions</span>
                      ) : exp.source === 'loan' ? (
                        <span className="text-xs text-muted-foreground">Manage in Loans</span>
                      ) : (
                        <>
                          <Button variant="ghost" size="sm" onClick={() => openEdit(exp)}>Edit</Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-destructive"
                            onClick={() => { if (confirm('Delete this expense?')) deleteMutation.mutate(exp.id); }}
                          >
                            Delete
                          </Button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {/* Pagination */}
      {pagination && pagination.totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            {pagination.total} expense{pagination.total !== 1 ? 's' : ''}
          </p>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="text-sm">Page {page} of {pagination.totalPages}</span>
            <Button variant="outline" size="sm" disabled={page >= pagination.totalPages} onClick={() => setPage(p => p + 1)}>
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Create / Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent key={editingExpense?.id || 'new'} className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingExpense ? 'Edit Expense' : 'New Expense'}</DialogTitle>
            <DialogDescription>
              {editingExpense ? 'Update the expense details below.' : 'Enter the details for the new expense.'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="categoryId">Category *</Label>
                <select
                  name="categoryId"
                  id="categoryId"
                  required
                  value={formCategoryId}
                  onChange={(e) => setFormCategoryId(e.target.value)}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                >
                  <option value="">Select category</option>
                  {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div>
                <Label htmlFor="businessProfileId">Business Profile</Label>
                <select
                  name="businessProfileId"
                  id="businessProfileId"
                  defaultValue={editingExpense?.businessProfileId || ''}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                >
                  <option value="">None (General)</option>
                  {profiles.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </div>
            </div>
            <div>
              <Label htmlFor="description">Description *</Label>
              <Input name="description" id="description" required defaultValue={editingExpense?.description || ''} />
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label htmlFor="amount">Amount *</Label>
                <Input
                  type="number"
                  name="amount"
                  id="amount"
                  step="0.01"
                  min="0.01"
                  required
                  defaultValue={editingExpense?.amount || ''}
                />
              </div>
              <div>
                <Label htmlFor="expenseDate">Date</Label>
                <Input
                  type="date"
                  name="expenseDate"
                  id="expenseDate"
                  defaultValue={editingExpense?.expenseDate ? editingExpense.expenseDate.split('T')[0] : new Date().toISOString().split('T')[0]}
                />
              </div>
              <div>
                <Label htmlFor="vendor">Vendor</Label>
                <Input name="vendor" id="vendor" defaultValue={editingExpense?.vendor || ''} />
              </div>
            </div>
            <div>
              <Label htmlFor="referenceNumber">Reference / Receipt #</Label>
              <Input name="referenceNumber" id="referenceNumber" defaultValue={editingExpense?.referenceNumber || ''} />
            </div>
            <div className="flex items-center gap-6">
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  name="isRecurring"
                  id="isRecurring"
                  checked={formIsRecurring}
                  onChange={(e) => setFormIsRecurring(e.target.checked)}
                  className="h-4 w-4"
                />
                <Label htmlFor="isRecurring">Recurring</Label>
              </div>
              <div>
                <select
                  name="recurrenceFrequency"
                  id="recurrenceFrequency"
                  defaultValue={editingExpense?.recurrenceFrequency || ''}
                  className="flex h-10 rounded-md border border-input bg-background px-3 py-2 text-sm"
                >
                  <option value="">Frequency</option>
                  <option value="weekly">Weekly</option>
                  <option value="monthly">Monthly</option>
                  <option value="quarterly">Quarterly</option>
                  <option value="annual">Annual</option>
                </select>
              </div>
              <div className="flex items-center gap-2">
                <Label htmlFor="paymentDueDay" className="whitespace-nowrap">Due Day</Label>
                <Input
                  type="number"
                  name="paymentDueDay"
                  id="paymentDueDay"
                  min="1"
                  max="31"
                  placeholder="1-31"
                  className="w-20"
                  defaultValue={editingExpense?.paymentDueDay || ''}
                />
              </div>
            </div>
            {(() => {
              const slug = categories.find(c => c.id === formCategoryId)?.slug;
              const blocked = slug ? BLOCKED_CARD_CATEGORY_SLUGS.has(slug) : false;
              if (!formIsRecurring) return null;
              if (blocked) {
                return (
                  <div className="text-xs text-muted-foreground bg-muted/30 border rounded px-3 py-2">
                    This category (mortgage, loans, or credit card interest) cannot be paid with a credit card.
                  </div>
                );
              }
              return (
                <div>
                  <Label htmlFor="chargeAccountId">Paid with (Credit Card)</Label>
                  <select
                    name="chargeAccountId"
                    id="chargeAccountId"
                    defaultValue={editingExpense?.chargeAccountId || ''}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  >
                    <option value="">Not on a credit card</option>
                    {chargeAccounts.map(ca => (
                      <option key={ca.id} value={ca.id}>
                        {ca.name}{ca.last4 ? ` ****${ca.last4}` : ''}
                      </option>
                    ))}
                  </select>
                  {chargeAccounts.length === 0 && (
                    <p className="text-xs text-muted-foreground mt-1">
                      No active charge accounts. Add one on the Charge Accounts page to allocate bills.
                    </p>
                  )}
                </div>
              );
            })()}
            <div className="flex items-center gap-6">
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  name="isTaxDeductible"
                  id="isTaxDeductible"
                  defaultChecked={editingExpense?.isTaxDeductible || false}
                  className="h-4 w-4"
                />
                <Label htmlFor="isTaxDeductible">Tax Deductible</Label>
              </div>
              {(() => {
                const selectedCat = categories.find(c => c.id === formCategoryId);
                if (!selectedCat) {
                  return (
                    <p className="flex-1 text-xs text-muted-foreground">
                      Pick a category — tax form line is derived automatically.
                    </p>
                  );
                }
                if (selectedCat.isInventory) {
                  return (
                    <p className="flex-1 text-xs text-muted-foreground">
                      Inventory: reported as Cost of Goods Sold (Schedule C Part III / Form 1125-A).
                    </p>
                  );
                }
                if (!selectedCat.taxDeductionClass) {
                  return (
                    <p className="flex-1 text-xs text-amber-600">
                      No tax classification set on this category.
                    </p>
                  );
                }
                return (
                  <p className="flex-1 text-xs text-muted-foreground">
                    Tax form line: <span className="font-medium text-foreground">{selectedCat.taxDeductionClass}</span>
                    <span className="ml-1">(derived from category)</span>
                  </p>
                );
              })()}
            </div>
            {/* Linked Asset (optional). When linked to a vehicle, show
                method + miles fields for Schedule C Part IV / Form 4562 detail. */}
            <div className="rounded-md border p-3 bg-muted/30 space-y-3">
              <div>
                <Label htmlFor="assetId">Linked Asset (optional)</Label>
                <select
                  id="assetId"
                  value={formAssetId}
                  onChange={(e) => {
                    setFormAssetId(e.target.value);
                    // Clear vehicle fields if switching away from a vehicle
                    const newAsset = assets.find(a => a.id === e.target.value);
                    if (!newAsset || newAsset.assetType !== 'vehicle') {
                      setFormVehicleMethod('');
                    }
                  }}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                >
                  <option value="">— None —</option>
                  {assets.map(a => (
                    <option key={a.id} value={a.id}>
                      {a.name}{a.assetType === 'vehicle' ? ' (vehicle)' : ` (${a.assetType})`}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-muted-foreground mt-1">
                  Tag this expense to an asset (e.g., a specific vehicle) so vehicle expenses
                  roll up correctly on Schedule C Part IV / Form 4562.
                </p>
              </div>

              {(() => {
                const linkedAsset = assets.find(a => a.id === formAssetId);
                if (!linkedAsset || linkedAsset.assetType !== 'vehicle') return null;
                return (
                  <>
                    <div>
                      <Label>Method (vehicle deduction)</Label>
                      <div className="flex items-center gap-4 mt-1">
                        <label className="flex items-center gap-2 text-sm">
                          <input
                            type="radio"
                            name="vehicleMethod"
                            value="mileage"
                            checked={formVehicleMethod === 'mileage'}
                            onChange={() => setFormVehicleMethod('mileage')}
                          />
                          Standard mileage rate
                        </label>
                        <label className="flex items-center gap-2 text-sm">
                          <input
                            type="radio"
                            name="vehicleMethod"
                            value="actual"
                            checked={formVehicleMethod === 'actual'}
                            onChange={() => setFormVehicleMethod('actual')}
                          />
                          Actual cost
                        </label>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">
                        Pick once per vehicle and stick with it for the year. Mixing methods on
                        the same vehicle triggers a Schedule C Part IV warning.
                      </p>
                    </div>

                    {formVehicleMethod === 'mileage' && (
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label htmlFor="businessMiles">Business Miles</Label>
                          <Input
                            type="number"
                            name="businessMiles"
                            id="businessMiles"
                            min="0"
                            step="1"
                            placeholder="e.g. 4200"
                            defaultValue={editingExpense?.businessMiles ?? ''}
                          />
                        </div>
                        <div>
                          <Label htmlFor="totalMiles">Total Miles (optional)</Label>
                          <Input
                            type="number"
                            name="totalMiles"
                            id="totalMiles"
                            min="0"
                            step="1"
                            placeholder="e.g. 8500"
                            defaultValue={editingExpense?.totalMiles ?? ''}
                          />
                        </div>
                      </div>
                    )}
                  </>
                );
              })()}
            </div>

            <div>
              <Label htmlFor="notes">Notes</Label>
              <Textarea name="notes" id="notes" rows={2} defaultValue={editingExpense?.notes || ''} />
            </div>
            {editingExpense ? (
              <ExpenseReceiptsPanel expenseId={editingExpense.id} />
            ) : (
              <p className="text-xs text-muted-foreground border rounded-md p-3">
                Save this expense to attach receipts and supporting documents.
              </p>
            )}
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={createMutation.isPending || updateMutation.isPending}>
                {(createMutation.isPending || updateMutation.isPending) && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                {editingExpense ? 'Update' : 'Create'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <ExpenseAuditReportDialog
        open={auditDialogOpen}
        onOpenChange={setAuditDialogOpen}
        profiles={profiles}
        categories={categories}
        defaultDateFrom={dateFrom || new Date(new Date().getFullYear(), 0, 1).toISOString().slice(0, 10)}
        defaultDateTo={dateTo || new Date().toISOString().slice(0, 10)}
      />

      {/* Import CSV Dialog */}
      <Dialog open={importDialogOpen} onOpenChange={setImportDialogOpen}>
        <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Import Expenses from CSV</DialogTitle>
            <DialogDescription>
              Upload a CSV file. The system will validate every row before importing — you'll see errors first and only valid rows get inserted.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* File picker */}
            <div className="border-2 border-dashed rounded-lg p-6 text-center">
              <Upload className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
              <Label htmlFor="csv-upload" className="cursor-pointer">
                <span className="text-sm font-medium text-primary hover:underline">
                  Click to select a CSV file
                </span>
                <Input
                  id="csv-upload"
                  type="file"
                  accept=".csv,text/csv"
                  className="hidden"
                  onChange={handleFileSelected}
                />
              </Label>
              {importFileName && (
                <p className="text-xs text-muted-foreground mt-2">Selected: {importFileName}</p>
              )}
              <p className="text-xs text-muted-foreground mt-2">
                Need the format? Use the "Download Template" button on the main page.
              </p>
            </div>

            {/* Parse results */}
            {importParseResult && (
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <Badge variant="success">{importParseResult.valid.length} valid</Badge>
                  {importParseResult.invalid.length > 0 && (
                    <Badge variant="destructive">{importParseResult.invalid.length} with errors</Badge>
                  )}
                </div>

                {/* Errors */}
                {importParseResult.invalid.length > 0 && (
                  <div className="border rounded-lg p-3 bg-red-500/5">
                    <div className="flex items-center gap-2 mb-2 text-sm font-medium text-red-600">
                      <AlertCircle className="h-4 w-4" />
                      Rows with errors (will not be imported)
                    </div>
                    <div className="space-y-2 max-h-48 overflow-y-auto">
                      {importParseResult.invalid.map((item) => (
                        <div key={item.row} className="text-xs border-b pb-2 last:border-b-0">
                          <span className="font-medium">Row {item.row}:</span>
                          <span className="ml-1 text-muted-foreground">
                            {item.raw.Description || '(no description)'}
                          </span>
                          <ul className="mt-1 ml-4 list-disc text-red-600">
                            {item.errors.map((err, i) => (
                              <li key={i}>{err}</li>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Valid preview */}
                {importParseResult.valid.length > 0 && (
                  <div className="border rounded-lg overflow-hidden">
                    <div className="px-3 py-2 bg-muted/50 text-sm font-medium">
                      Preview ({importParseResult.valid.length} row{importParseResult.valid.length !== 1 ? 's' : ''} ready)
                    </div>
                    <div className="max-h-64 overflow-y-auto">
                      <table className="w-full text-xs">
                        <thead className="bg-muted/30 sticky top-0">
                          <tr>
                            <th className="text-left p-2">Description</th>
                            <th className="text-right p-2">Amount</th>
                            <th className="text-left p-2">Date</th>
                            <th className="text-left p-2">Vendor</th>
                          </tr>
                        </thead>
                        <tbody>
                          {importParseResult.valid.slice(0, 50).map((row, i) => (
                            <tr key={i} className="border-t">
                              <td className="p-2">{row.description}</td>
                              <td className="p-2 text-right">{formatCurrency(row.amount)}</td>
                              <td className="p-2">{row.expenseDate}</td>
                              <td className="p-2">{row.vendor || '—'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      {importParseResult.valid.length > 50 && (
                        <div className="p-2 text-center text-xs text-muted-foreground bg-muted/30">
                          ... and {importParseResult.valid.length - 50} more
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setImportDialogOpen(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleImportConfirm}
                disabled={!importParseResult || importParseResult.valid.length === 0 || importMutation.isPending}
              >
                {importMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Import {importParseResult?.valid.length || 0} Expense{importParseResult?.valid.length !== 1 ? 's' : ''}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
